import { CardMediaPill } from '@app/components/base/CardMediaPill';
import { UploadedPlatformsStatus } from '@app/modules/social-export/components/UploadedPlatformsStatus';
import { AppCardMediaOverlay } from '@app/components/base/app-card';
import { EnrichedVideoEditorEditListItemDto } from '@app/modules/video-editions/types';
import { Box } from '@mui/material';
import { getPublishedPlatformDisplayItems } from '../../lib/get-published-platform-display-items';
import { EditCardMediaBlueprintGrid } from './EditCardMediaBlueprintGrid';
import { EditCardMediaEditorHoverOverlay } from './EditCardMediaEditorHoverOverlay';
import { EditCardMediaPlaceholder } from './EditCardMediaPlaceholder';
import { EditCardRenderedVideoPreview } from './EditCardRenderedVideoPreview';
import { EditCardRenderStatus } from './EditCardRenderStatus';
import { EditCardThumbnail } from './EditCardThumbnail';

export interface EditCardMediaAreaProps {
  thumbnailSrc: string | null;
  renderJob: EnrichedVideoEditorEditListItemDto['renderJob'];
  publishedPlatforms: EnrichedVideoEditorEditListItemDto['publishedPlatforms'];
  /** When set (generated edit with playback URL), thumbnail area gets hover play → inline player. */
  renderedPlaybackUrl: string | null;
  /** When there is no playback preview, hover shows edit (pencil) like the card Edit action. */
  editorUrl: string;
  /** Subtle blueprint-style grid on the media until render is completed. */
  showBlueprintGrid: boolean;
}

/**
 * Top of the card: thumbnail or empty placeholder; top-right shows either upload platform
 * statuses (when there is a completed/failed publish to show) or render job / “Sin generar”.
 */
export function EditCardMediaArea({
  thumbnailSrc,
  renderJob,
  publishedPlatforms,
  renderedPlaybackUrl,
  editorUrl,
  showBlueprintGrid,
}: EditCardMediaAreaProps) {
  const platformItems = getPublishedPlatformDisplayItems(publishedPlatforms);

  const hasPublishedPlatformStatuses = platformItems.length > 0;

  const baseMedia = thumbnailSrc ? (
    <EditCardThumbnail src={thumbnailSrc} />
  ) : (
    <EditCardMediaPlaceholder />
  );

  const media = showBlueprintGrid ? (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {baseMedia}
      <EditCardMediaBlueprintGrid />
    </Box>
  ) : (
    baseMedia
  );

  return (
    <Box sx={{ position: 'relative', flexShrink: 0 }}>
      <Box sx={{ position: 'relative', zIndex: 0, flexShrink: 0 }}>
        {renderedPlaybackUrl ? (
          <EditCardRenderedVideoPreview playbackUrl={renderedPlaybackUrl}>
            {media}
          </EditCardRenderedVideoPreview>
        ) : (
          <EditCardMediaEditorHoverOverlay editorUrl={editorUrl}>
            {media}
          </EditCardMediaEditorHoverOverlay>
        )}
      </Box>
      <AppCardMediaOverlay overlayPosition="top-right">
        {hasPublishedPlatformStatuses ? (
          <CardMediaPill>
            <UploadedPlatformsStatus variant="pill" size="medium" items={platformItems} />
          </CardMediaPill>
        ) : (
          <EditCardRenderStatus renderJob={renderJob} />
        )}
      </AppCardMediaOverlay>
    </Box>
  );
}
